import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { FileText, Edit3 } from "lucide-react";
import { toast } from "sonner";

export default function ScopeDialog({ open, onOpenChange, client, isAdmin, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(client?.scope || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(client?.scope || "");
    setEditing(false);
  }, [client?.scope, open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${client.id}`, { scope: draft });
      toast.success("Scope updated");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const hasScope = (client?.scope || "").trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-zinc-800 max-w-2xl" data-testid="scope-dialog">
        <DialogHeader>
          <div className="t-eyebrow mb-2"><FileText className="w-3 h-3 inline mr-1" /> Contract scope</div>
          <DialogTitle className="font-display text-[20px] tracking-tight font-semibold">{client?.name}</DialogTitle>
          <p className="text-zinc-500 text-[12px] pt-1">Monthly deliverables &amp; agreement.</p>
        </DialogHeader>

        {!editing ? (
          hasScope ? (
            <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md p-4 text-[13px] text-zinc-200 leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto" data-testid="scope-content">
              {client.scope}
            </div>
          ) : (
            <div className="bg-[#0a0a0a] border border-dashed border-zinc-800 rounded-md p-6 text-center text-zinc-600 text-[12px]" data-testid="scope-empty">
              No scope documented yet. {isAdmin && "Click 'Edit' to add the monthly deliverables and contract terms."}
            </div>
          )
        ) : (
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14}
            data-testid="scope-textarea"
            placeholder={`e.g.\n• 8 reels per month\n• 16 static posts\n• 2 strategy calls\n• 24h turnaround on revisions\n• Pricing: ₹X / month`}
            className="bg-[#0a0a0a] border-zinc-800 text-[13px] leading-relaxed font-mono" />
        )}

        <DialogFooter className="gap-2">
          {!editing ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400 text-[12px] h-9">Close</Button>
              {isAdmin && (
                <Button onClick={() => setEditing(true)} data-testid="scope-edit-btn"
                  className="bg-white hover:bg-zinc-200 text-black h-9 text-[12px]">
                  <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {hasScope ? "Edit" : "Add scope"}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => { setEditing(false); setDraft(client?.scope || ""); }}
                className="text-zinc-400 text-[12px] h-9">Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="scope-save-btn"
                className="bg-white hover:bg-zinc-200 text-black h-9 text-[12px]">Save</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
